import type React from "react"
import { useMemo } from "react"
import { GdeltClusterDrawer } from "../GdeltClusterDrawer"
import {
  DEFAULT_GDELT_SORT_MODEL,
  PRECOMPUTED_MAP_RESULT_LIMIT,
} from "../constants"
import { getBiothreatV2GdeltColumns } from "./BiothreatV2Columns"
import { BiothreatV2GdeltFilters } from "./BiothreatV2Filters"
import { GDELT_SORTABLE_COLUMNS } from "../PrecomputedSortableColumns"
import { usePrecomputedBiothreatSignals } from "../PrecomputedBiothreatSignalsContext"
import { usePrecomputedGdeltSignals } from "../usePrecomputedGdeltSignals"
import {
  usePrecomputedGridPage,
  usePrecomputedGridPageResetKey,
} from "../usePrecomputedGridPage"
import { BiothreatV2Body } from "./BiothreatV2Body"

export const BiothreatV2GdeltView: React.FC = () => {
  const {
    addPinnedSignal,
    pinnedSignals,
    gdeltFilters,
    dateRange,
    gdeltSortModel,
    setGdeltSortModel,
    pageSize,
  } = usePrecomputedBiothreatSignals()
  const pageResetKey = usePrecomputedGridPageResetKey(
    gdeltFilters,
    dateRange,
    pageSize
  )
  const { page, setPage } = usePrecomputedGridPage(pageResetKey)
  const { data, isLoading } = usePrecomputedGdeltSignals({
    page,
    sortModel: gdeltSortModel,
  })
  // The rail summary (aggregation strip / Top Diseases) reflects the whole
  // filtered set — including the impact-score floor — not the grid page.
  const { data: summaryData } = usePrecomputedGdeltSignals({
    page: 0,
    sortModel: gdeltSortModel,
    limit: PRECOMPUTED_MAP_RESULT_LIMIT,
  })
  // The map needs the full "noise" field, so it ignores the impact floor and
  // applies the threshold client-side for the sweep reveal.
  const { data: mapData } = usePrecomputedGdeltSignals({
    page: 0,
    sortModel: gdeltSortModel,
    limit: PRECOMPUTED_MAP_RESULT_LIMIT,
    impactScoreMinOverride: null,
  })

  const rows = data?.results ?? []
  const summaryRows = summaryData?.results ?? []
  const mapRows = mapData?.results ?? []
  const pinnedIds = useMemo(
    () => new Set(pinnedSignals.keys()),
    [pinnedSignals]
  )
  const columns = useMemo(
    () => getBiothreatV2GdeltColumns({ pinnedIds, onPin: addPinnedSignal }),
    [addPinnedSignal, pinnedIds]
  )

  return (
    <BiothreatV2Body
      kind="gdelt"
      rows={rows}
      summaryRows={summaryRows}
      mapRows={mapRows}
      rowCount={data?.count ?? 0}
      isLoading={isLoading && !data}
      page={page}
      onPageChange={setPage}
      sortModel={gdeltSortModel}
      onSortModelChange={setGdeltSortModel}
      sortableColumns={GDELT_SORTABLE_COLUMNS}
      defaultSortModel={DEFAULT_GDELT_SORT_MODEL}
      columns={columns}
      filters={<BiothreatV2GdeltFilters />}
      renderDrawer={(signal, onClose) => (
        <GdeltClusterDrawer
          cluster={signal}
          onClose={onClose}
          variant="command"
        />
      )}
    />
  )
}
